import { useCallback } from 'react';
import { useGetLayoutStylesParams } from 'root/hooks/useGetLayoutStylesParams';
import type { PopulatedMenu } from 'root/apiTypes';
import type { NavigateToFn, NavigateToParams } from 'root/clientTypes';
import {
  OverflowBehavior,
  TabsVariant,
  type DropdownOptionProps,
  type TabItem,
  type TabsProps,
} from 'wix-ui-tpa/cssVars';
import { useGetNavigationLayout } from 'root/hooks/preset/useGetNavigationLayout';
import { useGetNavigationFullWidthTab } from 'root/hooks/preset/useGetNavigationFullWidthTab';
import { buildMenuUrlFromCurrentUrl } from 'root/utils/navigation';
import { useExperiments, type ILocation, useTranslation } from '@wix/yoshi-flow-editor';
import { MenuListNavigation, Overflow } from 'root/consts';
import { useNavigationBi } from './Navigation.bi';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from 'root/components/Menus/stylesParams';
import { getNavigationTextAlignment } from 'root/components/Menus/stylesParamsUtils';
import { useMenusContext } from 'root/contexts/MenusContext';

type UseNavigationLogicParams = {
  menus: PopulatedMenu[];
  setActiveItem: (index: number) => void;
  navigateTo: NavigateToFn;
  currentUrl: ILocation['url'];
  isRow?: boolean;
};

export const useNavigationLogic = ({
  menus,
  setActiveItem,
  navigateTo,
  currentUrl,
  isRow,
}: UseNavigationLogicParams) => {
  const { preset, tabsOverflow } = useGetLayoutStylesParams();
  const { reportMenuNavigationClick } = useNavigationBi();
  const styles = useStyles();
  const { isRTL, isMobile } = useMenusContext();
  const { t } = useTranslation();

  const navigationDropdownStyle = styles.get(stylesParams.navigationDropdownStyle);
  const navigationTextAlignment = getNavigationTextAlignment({
    preset,
    isRTL,
    isMobile,
    gridMenuNavigationAlignment: styles.get(stylesParams.gridMenuNavigationAlignment),
    columnsMenuNavigationAlignment: styles.get(stylesParams.columnsMenuNavigationAlignment),
    sideBySideMenuNavigationAlignment: styles.get(stylesParams.sideBySideMenuNavigationAlignment),
    expandStripMenuNavigationAlignment: styles.get(stylesParams.expandStripMenuNavigationAlignment),
    isRow,
  });

  const navigationLayout = useGetNavigationLayout(preset);
  const navigationFullWidthTab = useGetNavigationFullWidthTab(preset);
  const { experiments } = useExperiments();
  const isTabsPanelExperimentEnabled = experiments.enabled('specs.restaurants.menusOoiTabsPanel');
  const isDropdownBottomExperimentEnabled = experiments.enabled('specs.restaurants.menusOoiDropdownBottom');
  const isLocationURLQueryParamExperimentEnabled = experiments.enabled('specs.restaurants.locationURLQueryParam');
  const renderButHideTabsForSeo = navigationLayout === MenuListNavigation.Dropdown;

  const getMenuNavigationDetailsByIndex = useCallback(
    (index: number): NavigateToParams => {
      const menu = menus[index];
      const menuId = menu.id;
      const locationName = isLocationURLQueryParamExperimentEnabled
        ? menu.businessLocationDetails?.name ?? ''
        : undefined;
      const urlQueryParam = menu.urlQueryParam;
      return { currentUrl, menuId, urlQueryParam, isFirst: index === 0, locationName };
    },
    [currentUrl, menus, isLocationURLQueryParamExperimentEnabled]
  );

  const tabsWidth = navigationFullWidthTab ? TabsVariant.fullWidth : TabsVariant.fit;
  const tabOptions = menus.map((menu, index): TabItem => {
    return {
      title: menu?.name,
      id: index.toString(),
      href: buildMenuUrlFromCurrentUrl(getMenuNavigationDetailsByIndex(index)),
    };
  });

  const dropdownOptions: DropdownOptionProps[] = tabOptions.map(
    (option): DropdownOptionProps => ({
      id: option.id,
      value: option.title,
      isSelectable: true,
    })
  );

  const selectMenu = (index: number) => {
    const menu = getMenuNavigationDetailsByIndex(index);
    setActiveItem(index);
    navigateTo(menu);
    reportMenuNavigationClick({
      navigationLayout,
      numMenus: menus.length,
    });
  };

  const onClickTab: TabsProps['onTabClick'] = (index, e) => {
    e.preventDefault();
    selectMenu(index);
  };

  const onClickNavigationDropdownOption = (option: DropdownOptionProps) => {
    selectMenu(Number(option.id));
  };

  const overflowBehavior =
    tabsOverflow === Overflow.Wrap && isTabsPanelExperimentEnabled ? OverflowBehavior.wrap : OverflowBehavior.scroll;

  const isDropdown = navigationLayout === MenuListNavigation.Dropdown;
  return {
    navigationLayout,
    tabsWidth,
    dropdownOptions,
    tabOptions,
    onClickNavigationDropdownOption,
    onClickTab,
    navigationDropdownStyle,
    renderButHideTabsForSeo,
    overflowBehavior,
    navigationTextAlignment,
    isDropdown,
    dropdowmPlacement: isDropdownBottomExperimentEnabled ? 'bottom' : undefined,
    dropdownFlip: isDropdownBottomExperimentEnabled ? false : undefined,
    t,
  };
};
